import {
  Button,
  Content,
  FileUpload,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant
} from '@patternfly/react-core';
import * as React from 'react';
import { ProcessBodyPart, ProcessBodySpec, isProcessBodyComplete } from './ProcessBody';

export interface ProcessBodyModalProps {
  isOpen: boolean;
  /** The process's friendly name, for the title. */
  processName: string;
  /** The same line the row shows, repeated here so the sender knows what they are feeding. */
  description?: string;
  /** What to collect, and how it will be assembled. One field is rendered per part. */
  spec: ProcessBodySpec;
  /** True while the trigger is in flight; the caller owns it, since the call is its to make. */
  isSubmitting?: boolean;
  /** Called with the content of each part, keyed by part, unparsed and untrimmed. */
  onSubmit: (collected: Record<string, string>) => void;
  onClose: () => void;
}

/** What one field holds while it is being filled in. */
interface PartState {
  content: string;
  filename: string;
  isReading: boolean;
  error: string | null;
}

const EMPTY_PART: PartState = { content: '', filename: '', isReading: false, error: null };

/** Enough to say "that is the file I meant" without previewing the whole thing. */
const shapeOf = (content: string): string => {
  const lines = content.split('\n').length;
  const kb = content.length / 1024;
  const size = kb < 1 ? `${content.length} characters` : `${kb.toFixed(kb < 10 ? 1 : 0)} kB`;
  return `${size}, ${lines} line${lines === 1 ? '' : 's'}`;
};

/**
 * Collects the body for a process that needs one, either pasted in or loaded from a file.
 *
 * One control does both: PatternFly's FileUpload in `text` mode is a text area with a browse
 * button and a drop target attached, and reads whatever is dropped straight into the area. So a
 * report can be uploaded, or its contents pasted from wherever they already are, and either way
 * what the sender sees before pressing Run is the exact bytes that go on the wire. Editing after
 * upload is allowed on purpose - fixing one bad row beats re-exporting the report.
 *
 * A process that needs several files gets one such control per part, and the Run button waits for
 * all of them: half a body is not something to send to a process that asked for two files. The
 * parts are handed back to the caller as they stand, keyed by part, and it is `buildProcessBody`
 * that decides what the request body looks like - this component neither parses nor trims them,
 * because they belong to the automation system, not to this application, and anything this layer
 * "helpfully" corrected would be a difference between what was reviewed here and what runs there.
 *
 * Mount it while it is open and unmount it when it closes, as AutomationProcesses does. The
 * content lives in this component's state, so that - rather than a reset on close - is what
 * guarantees the next process to ask for a body does not open on the last one's report.
 */
export const ProcessBodyModal: React.FunctionComponent<ProcessBodyModalProps> = ({
  isOpen,
  processName,
  description,
  spec,
  isSubmitting = false,
  onSubmit,
  onClose
}) => {
  const [parts, setParts] = React.useState<Record<string, PartState>>({});

  const stateOf = (key: string): PartState => parts[key] ?? EMPTY_PART;
  const update = (key: string, change: Partial<PartState>) =>
    setParts(prev => ({ ...prev, [key]: { ...EMPTY_PART, ...prev[key], ...change } }));

  const collected: Record<string, string> = {};
  spec.parts.forEach(({ key }) => {
    collected[key] = stateOf(key).content;
  });

  const isReading = spec.parts.some(({ key }) => stateOf(key).isReading);
  const canSubmit = isProcessBodyComplete(spec, collected) && !isReading && !isSubmitting;

  /**
   * Only the single-part case can promise the body is what the field shows; a JSON body adds the
   * wrapper, so say which properties the parts arrive under rather than let the sender assume the
   * process receives the file alone.
   */
  const note = spec.format === 'json'
    ? `The content below is sent as one JSON object, each part under its own property `
      + `(${spec.parts.map(({ key }) => key).join(', ')}). Nothing inside a part is trimmed, `
      + `reformatted or validated on the way.`
    : 'Sent to the automation system exactly as it appears below - nothing is trimmed, '
      + 'reformatted or validated on the way.';

  return (
    <Modal
      variant={ModalVariant.large}
      isOpen={isOpen}
      onClose={onClose}
      aria-label={`Run ${processName}`}
    >
      <ModalHeader
        title={`Run ${processName}`}
        description={description}
      />
      <ModalBody>
        <Content component="p" style={{ color: 'var(--pf-v6-global--Color--200)' }}>
          {note}
        </Content>
        <Form onSubmit={(event) => { event.preventDefault(); if (canSubmit) onSubmit(collected); }}>
          {spec.parts.map((part: ProcessBodyPart) => {
            const { content, filename, isReading: isPartReading, error } = stateOf(part.key);
            const fieldId = `process-body-${part.key}`;
            return (
              <FormGroup label={part.label} isRequired fieldId={fieldId} key={part.key}>
                <FileUpload
                  id={fieldId}
                  type="text"
                  value={content}
                  filename={filename}
                  filenamePlaceholder="Drag and drop a file here, or upload one"
                  textAreaPlaceholder="Paste or type the content to send, or upload a file above"
                  browseButtonText="Upload"
                  // So one bad row can be fixed here instead of sending the report back to be re-exported.
                  allowEditingUploadedText
                  isLoading={isPartReading}
                  isDisabled={isSubmitting}
                  validated={error ? 'error' : 'default'}
                  spinnerAriaValueText="Reading the file"
                  // Matches the label, so each field is addressable by the part it holds.
                  aria-label={part.label}
                  // Undefined means no restriction, which is the default and deliberate - see `accept`.
                  dropzoneProps={part.accept ? { accept: part.accept } : undefined}
                  onFileInputChange={(_event, file) => update(part.key, { filename: file.name, error: null })}
                  onDataChange={(_event, value) => update(part.key, { content: value })}
                  onTextChange={(_event, value) => update(part.key, { content: value })}
                  onReadStarted={() => update(part.key, { isReading: true, error: null })}
                  onReadFinished={() => update(part.key, { isReading: false })}
                  onReadFailed={(_event, readError, file) => update(part.key, {
                    isReading: false,
                    content: '',
                    filename: '',
                    error: `${file.name} could not be read${readError.message ? `: ${readError.message}` : '.'}`
                  })}
                  onClearClick={() => update(part.key, { content: '', filename: '', error: null })}
                  /* PatternFly's own variable for the text area, not a minHeight on the container: the
                     container grows either way, but only this grows the area the content is read in,
                     and setting the wrong one leaves a report squeezed into six lines above a gap.
                     One part gets the whole modal; several share it, so each gets less. */
                  style={{
                    '--pf-v6-c-file-upload__file-details__c-form-control--MinHeight':
                      spec.parts.length > 1 ? '10rem' : '20rem'
                  } as React.CSSProperties}
                />
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem variant={error ? 'error' : 'default'}>
                      {error
                        ?? (content.trim().length === 0
                          ? part.helperText ?? 'Upload a file, or paste its contents.'
                          : `${shapeOf(content)}${part.helperText ? ` - ${part.helperText}` : ''}`)}
                    </HelperTextItem>
                  </HelperText>
                </FormHelperText>
              </FormGroup>
            );
          })}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => onSubmit(collected)}
          isLoading={isSubmitting}
          isDisabled={!canSubmit}
        >
          {isSubmitting ? 'Running' : 'Run Process'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
